import { extendLocale } from '@nuxt/ui/composables/defineLocale';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserLocale } from '#layers/base/public';
import { buildNuxtUiOverrides, resolveNuxtUiLocale } from '#layers/ui/utils/nuxt-ui-locale';

/**
 * Reactive Nuxt UI `Locale`: bundled base for the active UI language, deep-merged
 * with the app-controlled override subtree. Recomputes when the UI language
 * (and therefore the resolved translations) changes.
 *
 * The object carries both halves of the locale split, because Nuxt UI reads them separately:
 * `messages` and `dir` follow the UI language, while `code` is handed straight to reka's
 * `ConfigProvider` (`@nuxt/ui` `App.vue`) and reaches only value formatters and parsers —
 * `NumberField`, `Calendar`, `DateField` and friends. That one is the format locale.
 */
export function useNuxtUiLocale() {
	const { t } = useI18n();
	const { language, formatLocale } = useUserLocale();

	return computed(() => {
		// Nuxt UI's bundled locales are translation catalogs, so they key off the UI language.
		const base = resolveNuxtUiLocale(language.value);
		return extendLocale(base, { code: formatLocale.value, messages: buildNuxtUiOverrides(t) });
	});
}
